"use client";

import { CircleCheck as CheckCircle2, Search, MapPin, Globe } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Pricing() {
  const rooms = [{
    numeral: "02",
    name: "SEO Package",
    image: "https://www.asahisearch.com/wp-content/uploads/2026/07/post_techstart-solutions-website-redesign.webp?v=t64bacbaoe",
    srcset: "https://www.asahisearch.com/wp-content/uploads/2026/07/local_seo_project.png?v=b2v3pg6pbr-wvc-srcset",
    sizes: "https://www.asahisearch.com/wp-content/uploads/2026/07/local_seo_project.png?v=6te6zdhjbj-wvc-sizes",
    alt: "Google Search Console screenshot showing increase in SEO impressions and clicks on laptop screen",
    sqft: "Data Driven",
    guests: "Organic Growth",
    view: "Rank Higher",
    bed: "Keyword Focus",
    description: "Dominate search results with our comprehensive SEO strategies. We help your business get discovered by the customers who need you most.",
    amenities: [{
      icon: Search,
      label: "Keyword Research"
    }, {
      icon: CheckCircle2,
      label: "Content Strategy"
    }, {
      icon: CheckCircle2,
      label: "Performance Tracking"
    }],
    price: "From $299"
  }];
  return <section data-section-id="99" id="pricingcard" className="bg-background text-foreground ![padding:10px_0px_160px_0px]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <PageIntro />

        <div className="mt-20 md:mt-28 lg:mt-32 flex flex-col gap-24 md:gap-32 lg:gap-40">
          {rooms.map((room, i) => <RoomListing key={i} room={room} index={i} />)}
        </div>
      </div>
    </section>;
}
function PageIntro() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return;
}
type Room = {
  numeral: string;
  name: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
  sqft: string;
  guests: string;
  view: string;
  bed: string;
  description: string;
  amenities: {
    icon: React.ComponentType<{
      className?: string;
    }>;
    label: string;
  }[];
  price: string;
};
function RoomListing({
  room,
  index
}: {
  room: Room;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const isReversed = index % 2 === 1;
  return <div ref={ref} data-index={index} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
      <div className={`lg:col-span-7 ${isReversed ? "lg:order-2" : "lg:order-1"}`}>
        <div className="group relative overflow-hidden rounded-lg shadow-lg">
          <div className="relative aspect-[4/5] w-full overflow-hidden rounded-lg">
            <img src={room.image} data-wvc-srcset={room.srcset} data-wvc-sizes={room.sizes} alt={room.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]" />
          </div>
        </div>
      </div>

      <div className={`lg:col-span-5 ${isReversed ? "lg:order-1" : "lg:order-2"}`}>
        <div className="flex flex-col">
          

          <h2 className="mt-4 font-default font-bold leading-[1.05] tracking-tight text-foreground" style={{
          fontSize: "clamp(2.5rem, 4.5vw, 4rem)"
        }}>
            {room.name}
          </h2>

          <div className="mt-8 h-px w-16 bg-primary" />

          <dl className="mt-8 grid grid-cols-2 gap-x-8 gap-y-6">
            <MetaItem label="Build Type" value={room.sqft} />
            <MetaItem label="Control" value={room.guests} />
            <MetaItem label="Optimization" value={room.view} />
            <MetaItem label="Focus" value={room.bed} />
          </dl>

          <p className="mt-10 font-default italic font-light text-lg leading-relaxed text-muted-foreground">
            {room.description}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
            {room.amenities.map((amenity, i) => {
            const Icon = amenity.icon;
            return <div key={i} data-index={i} className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-primary" style={{
                strokeWidth: 1.25
              }} />
                  <span className="font-default text-xs tracking-[0.15em] uppercase text-muted-foreground">
                    {amenity.label}
                  </span>
                </div>;
          })}
          </div>

          <div className="mt-10">
            <span className="font-default font-bold text-primary" style={{
            fontSize: "clamp(1.75rem, 2.5vw, 2.25rem)"
          }}>
              From {room.price}
            </span>
            <span className="font-default text-sm text-muted-foreground ml-2">
              / month
            </span>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Button asChild size="lg" className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive has-[>svg]:px-4 rounded-lg font-default tracking-[0.2em] uppercase h-12 px-8 shadow-md bg-primary text-white !text-lg disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary/90">
              <Link to="/ContactUsPage">Get Started</Link>
            </Button>
            <Button asChild variant="ghost" size="lg" className="rounded-lg font-default text-xs tracking-[0.2em] uppercase h-12 px-8 border border-border hover:bg-accent">
              
            </Button>
          </div>
        </div>
      </div>
    </div>;
}
function MetaItem({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div>
      <dt className="font-default text-[0.65rem] tracking-[0.25em] uppercase text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-2 font-default font-semibold text-lg text-foreground">
        {value}
      </dd>
    </div>;
}